import { defineStore } from "pinia";
import api from "@/modules/api";
import { Commit } from "@/types/entity";
import { useMoment } from "@/stores/moment";
import { useMate } from "./mate";
import { StorageGet } from "@/tools/localStorage";
import { sessionStorageGet } from "@/tools/sessionStorage";
import { useScopedMateId } from "@/composable/useScopedMateId";

const PAGE_SIZE = 999;

interface MateCommitState {
  pending: {
    list: Commit[];
    pageNum: number;
    changed: boolean;
  };
  finished: {
    list: Commit[];
    pageNum: number;
    changed: boolean;
  };
  pendingCommit: {
    list: Commit[];
    pageNum: number;
    changed: boolean;
  };
  finishedCommit: {
    list: Commit[];
    pageNum: number;
    changed: boolean;
  };
}

export const useMateCommit = defineStore("mateCommit", {
  state: (): MateCommitState => {
    return {
      pending: {
        list: [],
        pageNum: 1,
        changed: false,
      },
      finished: {
        list: [],
        pageNum: 1,
        changed: false,
      },
      pendingCommit: {
        list: [],
        pageNum: 1,
        changed: false,
      },
      finishedCommit: {
        list: [],
        pageNum: 1,
        changed: false,
      },
    };
  },
  getters: {
    pendingCount(state) {
      return state.pending.list.length;
    },
  },
  actions: {
    async getPendingList() {
      const mateId = useMate().userMate?.id;
      if (!mateId) return;
      const userPendingList = useScopedMateId(async () => {
        const admirerList = await api.readAdmirerRequestPage({
          filter: {
            status: "pending",
            role: "receiver",
            mateId,
          },
        });
        this.pending.list = (admirerList.page.data as Commit[]).sort(
          (a, b) =>
            new Date(b.dtCreate).getTime() - new Date(a.dtCreate).getTime()
        );
      });
      return userPendingList();
    },
    async getFinishedList() {
      const mateId = useMate().userMate?.id;
      if (!mateId) return;
      const userFinishedList = useScopedMateId(async () => {
        const admirerList = await api.readAdmirerRequestPage({
          filter: {
            status: "finished",
            role: "receiver",
            mateId,
          },
        });
        this.finished.list = (admirerList.page.data as Commit[]).sort(
          (a, b) =>
            new Date(b.dtCreate).getTime() - new Date(a.dtCreate).getTime()
        );
      });
      return userFinishedList();
    },
    async getPendingCommitList() {
      const mateId = useMate().userMate?.id;
      if (!mateId) return;
      const userPendingCommitList = useScopedMateId(async () => {
        const admirerList = await api.readAdmirerRequestPage({
          filter: {
            status: "pending",
            role: "sender",
            mateId,
          },
        });
        this.pendingCommit.list = (admirerList.page.data as Commit[]).sort(
          (a, b) =>
            new Date(b.dtCreate).getTime() - new Date(a.dtCreate).getTime()
        );
      });
      return userPendingCommitList();
    },
    async getFinishedCommitList() {
      const mateId = useMate().userMate?.id;
      if (!mateId) return;
      const userFinishedCommitList = useScopedMateId(async () => {
        const admirerList = await api.readAdmirerRequestPage({
          filter: {
            status: "finished",
            role: "sender",
            mateId,
          },
        });
        this.finishedCommit.list = (admirerList.page.data as Commit[]).sort(
          (a, b) =>
            new Date(b.dtCreate).getTime() - new Date(a.dtCreate).getTime()
        );
      });
      return userFinishedCommitList();
    },
    async fetchAdmireRequestList(): Promise<Commit[]> {
      return this.fetchAdmireRequestList();
    },
    async fetchPendingList(): Promise<Commit[]> {
      const res = await api.readMateCommitPage({
        filter: { status: "pending", role: "receiver" },
        pagination: { page: 1, perPage: PAGE_SIZE },
      });
      this.pending.list = res.page.data as Commit[];
      const momentStore = useMoment();
      momentStore.updateMomentMap(this.pending.list.map((item) => item.moment));
      this.pending.changed = false;
      return this.pending.list;
    },
    async fetchFinishedList(): Promise<Commit[]> {
      const res = await api.readMateCommitPage({
        filter: { status: "finished", role: "receiver" },
        pagination: { page: 1, perPage: PAGE_SIZE },
      });
      this.finished.list = res.page.data as Commit[];
      const momentStore = useMoment();
      momentStore.updateMomentMap(
        this.finished.list.map((item) => item.moment)
      );
      this.finished.changed = false;
      return this.finished.list;
    },
    async fetchPendingCommit(): Promise<Commit[]> {
      const res = await api.readMateCommitPage({
        filter: { role: "creator", status: "pending" },
        pagination: { page: 1, perPage: PAGE_SIZE },
      });
      this.pendingCommit.list = res.page.data as Commit[];
      const momentStore = useMoment();
      momentStore.updateMomentMap(
        this.pendingCommit.list.map((item) => item.moment)
      );
      this.pendingCommit.changed = false;
      return this.pendingCommit.list;
    },
    async fetchFinishedCommit(): Promise<Commit[]> {
      const res = await api.readMateCommitPage({
        filter: { role: "creator", status: "finished" },
        pagination: { page: 1, perPage: PAGE_SIZE },
      });
      this.finishedCommit.list = res.page.data as Commit[];
      const momentStore = useMoment();
      momentStore.updateMomentMap(
        this.finishedCommit.list.map((item) => item.moment)
      );
      this.finishedCommit.changed = false;
      return this.finishedCommit.list;
    },
    async approve(commitId: number, isAdmire: boolean): Promise<void> {
      const userApprove = useScopedMateId(async () => {
        isAdmire
          ? await api.reviewAdmirer(commitId, "accepted")
          : await api.reviewMateCommit(commitId, "accepted");
        this.pending.list = this.pending.list.filter(
          (commit) => commit.id !== commitId
        );
        this.finished.changed = true;
      });
      return userApprove();
    },
    async reject(mateId: number, isAdmire: boolean): Promise<void> {
      const userReject = useScopedMateId(async () => {
        isAdmire
          ? await api.reviewAdmirer(mateId, "rejected")
          : await api.reviewMateCommit(mateId, "rejected");
        this.pending.list = this.pending.list.filter(
          (commit) => commit.id !== mateId
        );
        this.finished.changed = true;
      });
      return userReject();
    },
    disableCache() {
      this.pending.changed = true;
      this.finished.changed = true;
      this.pendingCommit.changed = true;
      this.finishedCommit.changed = true;
    },
    async createMateCommit(params: {
      mateId: string;
      toMateId: string; // uuid
      title: string;
      text: string;
      mediaIdzz: number[];
      messageIdzz: number[];
    }) {
      await api.createOneMateCommit(params);
    },
  },
});
